import React from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { EVENT_SIZES, MONTAGE_MESSAGES, MARQUES_PHONAK_GROUP, MARQUES_OTICON_GROUP, FORME_IMAGES_RIC, FORME_IMAGES_BTE, FORMES_EMBOUT, FORMES_EMBOUT_BTE, materialHasShore } from './orderFormConstants';
import { OrangeInfoBox, MatiereSelect, DureteSelect, CouleurSelect } from './OrderFormHelpers';
import FormeImageSelector from './FormeImageSelector';

// Small inline component: modifier checkboxes (Demi-épaulement, Évidée) applied to a chosen forme.
// Le demi-épaulement est désormais une forme à part entière (tuile « Canule Demi-épaulement »),
// il ne reste que le modificateur « Évidée ».
function FormeModifiers({ forme, evidee, setEvidee, testIdPrefix = 'mod-' }) {
  if (!forme) return null;
  return (
    <div className="mt-3 flex flex-col sm:flex-row gap-2" data-testid={`${testIdPrefix}forme-modifiers`}>
      <label className={`flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer transition-colors text-sm ${evidee ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
        <Checkbox checked={evidee} onCheckedChange={(c) => setEvidee(!!c)} data-testid={`${testIdPrefix}evidee-checkbox`} />
        <span>Évidée</span>
      </label>
    </div>
  );
}
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

export default function FormFormeMatiere({ form, compact, changedFields }) {
  const labelClass = compact ? 'label-overline mb-1 block' : 'label-overline mb-2 block';
  const sectionClass = compact
    ? 'bg-white border border-zinc-200 p-3 sm:p-4 rounded-sm'
    : 'bg-white border border-zinc-200 p-4 sm:p-6';
  const headingClass = compact
    ? 'font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200'
    : 'font-heading font-semibold text-base sm:text-lg mb-4 pb-2 border-b border-zinc-200';
  const gapClass = compact ? 'gap-3' : 'gap-4';
  const H = compact ? 'h3' : 'h2';
  const isChanged = (fieldKey) => changedFields && changedFields.has(fieldKey);
  const changedBorder = (fieldKey) => isChanged(fieldKey) ? 'ring-2 ring-red-500 border-red-400' : '';

  const showPerEar = form.isBilateral && form.fabricationType === 'embout_auditif' && form.emboutType !== 'silk';

  if (form.fabricationType !== 'embout_auditif') return null;

  return (
    <>
      {/* Forme et matière */}
      <div className={sectionClass}>
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-zinc-200">
          <H className="font-heading font-semibold text-sm sm:text-base lg:text-lg m-0 p-0 border-0">Forme et matière</H>
          {showPerEar && (
            <label className="flex items-center gap-2 cursor-pointer" data-testid="per-ear-toggle">
              <span className="text-xs text-zinc-500">Différent par oreille</span>
              <Switch checked={form.perEarDiff} onCheckedChange={form.setPerEarDiff} />
            </label>
          )}
        </div>

        {showPerEar && form.perEarDiff ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* OD column */}
            <div className="border border-zinc-200 rounded-sm p-3 space-y-3" data-testid="per-ear-od">
              <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider">OD (Oreille Droite)</p>
              <div>
                <FormeImageSelector
                  value={form.formeEmbout}
                  onChange={form.setFormeEmbout}
                  options={form.emboutType === 'bte' ? FORMES_EMBOUT_BTE : undefined}
                  images={form.emboutType === 'ecouteur_deporte' ? FORME_IMAGES_RIC : form.emboutType === 'bte' ? FORME_IMAGES_BTE : undefined}
                  label="Forme"
                  required
                  testIdPrefix="forme-od-tile-"
                />
                <FormeModifiers
                  forme={form.formeEmbout}
                  evidee={form.formeEvidee} setEvidee={form.setFormeEvidee}
                  testIdPrefix="od-"
                />
              </div>
              <div>
                <Label className={labelClass}>Matière *</Label>
                <MatiereSelect value={form.matiere} onChange={form.setMatiere} testId="matiere-od" />
              </div>
              {materialHasShore(form.matiere) && (
                <div>
                  <Label className={labelClass}>Dureté OD *</Label>
                  <DureteSelect matiere={form.matiere} value={form.durete} onChange={form.setDurete} testId="durete-od" />
                </div>
              )}
            </div>
            {/* OG column */}
            <div className="border border-zinc-200 rounded-sm p-3 space-y-3" data-testid="per-ear-og">
              <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider">OG (Oreille Gauche)</p>
              <div>
                <FormeImageSelector
                  value={form.formeEmboutOG}
                  onChange={form.setFormeEmboutOG}
                  options={form.emboutType === 'bte' ? FORMES_EMBOUT_BTE : undefined}
                  images={form.emboutType === 'ecouteur_deporte' ? FORME_IMAGES_RIC : form.emboutType === 'bte' ? FORME_IMAGES_BTE : undefined}
                  label="Forme"
                  required
                  testIdPrefix="forme-og-tile-"
                />
                <FormeModifiers
                  forme={form.formeEmboutOG}
                  evidee={form.formeEvideeOG} setEvidee={form.setFormeEvideeOG}
                  testIdPrefix="og-"
                />
              </div>
              <div>
                <Label className={labelClass}>Matière *</Label>
                <MatiereSelect value={form.matiereOG} onChange={form.setMatiereOG} testId="matiere-og" />
              </div>
              {materialHasShore(form.matiereOG) && (
                <div>
                  <Label className={labelClass}>Dureté OG *</Label>
                  <DureteSelect matiere={form.matiereOG} value={form.dureteOG} onChange={form.setDureteOG} testId="durete-og" />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${gapClass}`}>
            {form.emboutType !== 'silk' && (
              <div className="sm:col-span-2 lg:col-span-3">
                <FormeImageSelector
                  value={form.formeEmbout}
                  onChange={form.setFormeEmbout}
                  options={form.emboutType === 'bte' ? FORMES_EMBOUT_BTE : undefined}
                    images={form.emboutType === 'ecouteur_deporte' ? FORME_IMAGES_RIC : form.emboutType === 'bte' ? FORME_IMAGES_BTE : undefined}
                  label="Forme de l'embout"
                  required
                  testIdPrefix="forme-tile-"
                  changedBorderClass={isChanged('forme_embout') ? changedBorder('forme_embout') : ''}
                />
                <FormeModifiers
                  forme={form.formeEmbout}
                  evidee={form.formeEvidee} setEvidee={form.setFormeEvidee}
                  testIdPrefix=""
                />
              </div>
            )}
            {form.emboutType === 'silk' && (
              <div className="sm:col-span-2 lg:col-span-3 bg-blue-50 border border-blue-200 p-3 rounded-sm" data-testid="silk-forme-info">
                <p className="text-sm text-blue-800"><span className="font-semibold">Intra (Silk)</span> — forme unique standard. Aucune sélection de forme nécessaire.</p>
              </div>
            )}

            <div>
              <Label className={`${labelClass} ${isChanged('material') ? 'text-red-600 font-bold' : ''}`}>Matière *</Label>
              <MatiereSelect
                value={form.matiere}
                onChange={form.setMatiere}
                disabled={form.emboutType === 'silk'}
                exclude={form.emboutType === 'silk' ? ['silicone', 'resine_3d'] : []}
                testId="matiere-select"
                className={changedBorder('material')}
              />
              {form.emboutType === 'silk' && <p className="text-xs text-blue-700 mt-1" data-testid="silk-matiere-hint">Intra (Silk) : résine souple obligatoire.</p>}
            </div>

            {materialHasShore(form.matiere) && (
              <div>
                <Label className={`${labelClass} ${isChanged('durete') ? 'text-red-600 font-bold' : ''}`}>Dureté (Shore) *</Label>
                <DureteSelect matiere={form.matiere} value={form.durete} onChange={form.setDurete} testId="durete-select" className={changedBorder('durete')} />
              </div>
            )}

            {form.matiere === 'silicone' && (
              <div>
                <Label className={`${labelClass} ${isChanged('color') ? 'text-red-600 font-bold' : ''}`}>Couleur *</Label>
                <CouleurSelect shores={[form.durete]} value={form.couleur} onChange={form.setCouleur} exclude={['rouge_bleu']} testId="couleur-select" className={changedBorder('color')} />
              </div>
            )}

            <div>
              <Label className={`${labelClass} ${isChanged('etancheite') ? 'text-red-600 font-bold' : ''}`}>Étanchéité</Label>
              <Select value={form.etancheite} onValueChange={form.setEtancheite}>
                <SelectTrigger className={`rounded-sm bg-white ${changedBorder('etancheite')}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="sans">Sans</SelectItem>
                  <SelectItem value="renforce">Renforcé</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.isResineSouple && (
              <div className="sm:col-span-2 lg:col-span-3 p-3 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-900" data-testid="resine-souple-info">
                Résine souple : embout <strong>non laqué</strong> (pas de finition brillant/mat) et <strong>sans marquage spécial</strong> — seule la gravure du numéro de série sera présente.
              </div>
            )}
            {!form.isResineSouple && <div>
              <Label className={`${labelClass} ${isChanged('finition') ? 'text-red-600 font-bold' : ''}`}>Finition</Label>
              <Select value={form.finition} onValueChange={(v) => { form.setFinition(v); if (v !== 'brillant') form.setFinitionAnallergique(false); }}>
                <SelectTrigger className={`rounded-sm bg-white ${changedBorder('finition')}`} data-testid="finition-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="brillant">Brillant</SelectItem>
                  <SelectItem value="mat">Mat</SelectItem>
                </SelectContent>
              </Select>
            </div>}

            {!form.isResineSouple && form.finition === 'brillant' && (
              <div className="flex items-center gap-3 sm:col-span-2 lg:col-span-3">
                <label className={`flex items-center gap-3 p-2.5 sm:p-3 rounded-sm border cursor-pointer transition-colors w-full ${form.finitionAnallergique ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid="finition-anallergique">
                  <Checkbox checked={form.finitionAnallergique} onCheckedChange={form.setFinitionAnallergique} />
                  <span className="font-medium text-sm">Finition anallergique</span>
                </label>
              </div>
            )}
          </div>
        )}

        {/* Couleur / Étanchéité / Finition below per-ear */}
        {showPerEar && form.perEarDiff && (
          <div className={`grid grid-cols-1 sm:grid-cols-3 ${gapClass} mt-4`}>
            {(form.matiere === 'silicone' || form.matiereOG === 'silicone') && (
              <div>
                <Label className={`${labelClass} ${isChanged('color') ? 'text-red-600 font-bold' : ''}`}>Couleur *</Label>
                {/* Couleurs : uniquement si LES DEUX oreilles sont en silicone et disponibles dans les deux duretés.
                    Si l'une des deux est en résine, seul transparent est possible. */}
                {form.matiere === 'silicone' && form.matiereOG === 'silicone' ? (
                  <CouleurSelect shores={[form.durete, form.dureteOG]} value={form.couleur} onChange={form.setCouleur} exclude={['rouge_bleu']} testId="couleur-select-bilateral" className={changedBorder('color')} />
                ) : (
                  <CouleurSelect shores={[]} value={form.couleur} onChange={form.setCouleur} only={['transparent']} testId="couleur-select-bilateral" className={changedBorder('color')} />
                )}
              </div>
            )}
            <div>
              <Label className={`${labelClass} ${isChanged('etancheite') ? 'text-red-600 font-bold' : ''}`}>Étanchéité</Label>
              <Select value={form.etancheite} onValueChange={form.setEtancheite}>
                <SelectTrigger className={`rounded-sm bg-white ${changedBorder('etancheite')}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="sans">Sans</SelectItem>
                  <SelectItem value="renforce">Renforcé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.isResineSouple && (
              <div className="sm:col-span-2 lg:col-span-3 p-3 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-900" data-testid="resine-souple-info">
                Résine souple : embout <strong>non laqué</strong> (pas de finition brillant/mat) et <strong>sans marquage spécial</strong> — seule la gravure du numéro de série sera présente.
              </div>
            )}
            {!form.isResineSouple && <div>
              <Label className={`${labelClass} ${isChanged('finition') ? 'text-red-600 font-bold' : ''}`}>Finition</Label>
              <Select value={form.finition} onValueChange={(v) => { form.setFinition(v); if (v !== 'brillant') form.setFinitionAnallergique(false); }}>
                <SelectTrigger className={`rounded-sm bg-white ${changedBorder('finition')}`} data-testid="finition-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="brillant">Brillant</SelectItem>
                  <SelectItem value="mat">Mat</SelectItem>
                </SelectContent>
              </Select>
            </div>}
            {!form.isResineSouple && form.finition === 'brillant' && (
              <div className="sm:col-span-3">
                <label className={`flex items-center gap-3 p-2.5 sm:p-3 rounded-sm border cursor-pointer transition-colors w-full ${form.finitionAnallergique ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid="finition-anallergique-perear">
                  <Checkbox checked={form.finitionAnallergique} onCheckedChange={form.setFinitionAnallergique} />
                  <span className="font-medium text-sm">Finition anallergique</span>
                </label>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Event (Ventilation) — affiche uniquement les côtés commandés */}
      <div className={sectionClass}>
        <H className={headingClass}>Event (Ventilation)</H>
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${gapClass}`}>
          {(form.earSide === 'right' || form.earSide === 'both') && (
            <div>
              <Label className={`${labelClass} ${isChanged('event_od') ? 'text-red-600 font-bold' : ''}`}>Event OD (Oreille Droite)</Label>
              <Select value={form.eventOD} onValueChange={form.setEventOD}>
                <SelectTrigger className={`rounded-sm bg-white ${changedBorder('event_od')}`} data-testid="event-od-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{EVENT_SIZES.map(s => <SelectItem key={s} value={s}>{s}{s !== 'Sans' && s !== 'MAX' ? ' mm' : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {(form.earSide === 'left' || form.earSide === 'both') && (
            <div>
              <Label className={`${labelClass} ${isChanged('event_og') ? 'text-red-600 font-bold' : ''}`}>Event OG (Oreille Gauche)</Label>
              <Select value={form.eventOG} onValueChange={form.setEventOG}>
                <SelectTrigger className={`rounded-sm bg-white ${changedBorder('event_og')}`} data-testid="event-og-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{EVENT_SIZES.map(s => <SelectItem key={s} value={s}>{s}{s !== 'Sans' && s !== 'MAX' ? ' mm' : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {!form.earSide && (
            <p className="text-xs text-zinc-400 italic sm:col-span-2">Sélectionnez d'abord un côté (OD, OG ou bilatéral) pour choisir l'event.</p>
          )}
        </div>
      </div>

      {/* Montage BTE: depends on material (silicone → tube classique OR tube lock; resin → tube only) */}
      {form.fabricationType === 'embout_auditif' && form.emboutType === 'bte' && form.matiere && (
        <div className={sectionClass} data-testid="montage-section-bte">
          <H className={headingClass}>Options de montage BTE</H>
          {form.matiere === 'silicone' ? (
            <RadioGroup value={form.montageType || 'montage_tube'} onValueChange={form.setMontageType} className="space-y-2">
              <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${(form.montageType || 'montage_tube') === 'montage_tube' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                <RadioGroupItem value="montage_tube" id="montage-tube-bte" data-testid="montage-tube-bte" />
                <span className="font-medium text-sm">Montage tube classique</span>
              </label>
              <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${form.montageType === 'montage_tube_lock' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                <RadioGroupItem value="montage_tube_lock" id="montage-tube-lock-bte" data-testid="montage-tube-lock-bte" />
                <span className="font-medium text-sm">Montage tube lock</span>
              </label>
              <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${form.montageType === 'montage_tube_enfant' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                <RadioGroupItem value="montage_tube_enfant" id="montage-tube-enfant-bte" data-testid="montage-tube-enfant-bte" />
                <span className="font-medium text-sm">Tube enfant (diamètre 2,5 mm)</span>
              </label>
            </RadioGroup>
          ) : (
            <RadioGroup value={form.montageType || 'montage_tube'} onValueChange={form.setMontageType} className="space-y-2">
              <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${(form.montageType || 'montage_tube') === 'montage_tube' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                <RadioGroupItem value="montage_tube" id="montage-tube-bte-resin" data-testid="montage-tube-bte" />
                <span className="font-medium text-sm">Montage tube classique</span>
              </label>
              <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${form.montageType === 'montage_tube_enfant' ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}>
                <RadioGroupItem value="montage_tube_enfant" id="montage-tube-enfant-bte-resin" data-testid="montage-tube-enfant-bte" />
                <span className="font-medium text-sm">Tube enfant (diamètre 2,5 mm)</span>
              </label>
              <p className="text-xs text-zinc-500 mt-1">Le montage tube lock nécessite une matière silicone.</p>
            </RadioGroup>
          )}
          {form.montageType && MONTAGE_MESSAGES[form.montageType] && (
            <div className="mt-2">
              <OrangeInfoBox>{MONTAGE_MESSAGES[form.montageType]}</OrangeInfoBox>
            </div>
          )}

          {/* Brand-specific mini-tube — independent of the standard montage tube choice above */}
          {form.marque && (MARQUES_PHONAK_GROUP.includes(form.marque) || MARQUES_OTICON_GROUP.includes(form.marque)) && (
            <div className="mt-4 pt-4 border-t border-zinc-200">
              <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${form.bteBrandSpecificEnabled ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid="bte-brand-specific-checkbox">
                <Checkbox checked={form.bteBrandSpecificEnabled}
                  onCheckedChange={(v) => {
                    form.setBteBrandSpecificEnabled(!!v);
                    if (v) {
                      // Auto-select the right option for the brand group
                      if (MARQUES_PHONAK_GROUP.includes(form.marque)) {
                        form.setBteBrandSpecificType('power_slim_tube');
                        form.setBteCordaMinifitDiameter('');
                      } else if (MARQUES_OTICON_GROUP.includes(form.marque)) {
                        form.setBteBrandSpecificType('corda_minifit');
                        form.setBteCordaMinifitDiameter('0.9');
                      }
                    } else {
                      form.setBteBrandSpecificType('');
                      form.setBteCordaMinifitDiameter('');
                    }
                  }} />
                <div>
                  <span className="font-medium text-sm">Options spécifiques à une marque</span>
                  <p className="text-xs text-zinc-500 mt-0.5">Système mini-tube spécifique à la marque (indépendant du montage tube ci-dessus).</p>
                </div>
              </label>

              {form.bteBrandSpecificEnabled && MARQUES_PHONAK_GROUP.includes(form.marque) && (
                <div className="mt-3 ml-1 p-3 border border-zinc-200 rounded-sm bg-zinc-50/50" data-testid="bte-brand-sonova">
                  <Label className="label-overline mb-2 block">Groupe Sonova (Phonak / Unitron)</Label>
                  <RadioGroup value={form.bteBrandSpecificType} onValueChange={form.setBteBrandSpecificType} className="space-y-2">
                    <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors bg-white ${form.bteBrandSpecificType === 'power_slim_tube' ? 'border-zinc-900' : 'border-zinc-200 hover:border-zinc-400'}`}>
                      <RadioGroupItem value="power_slim_tube" id="bte-power-slim-tube" data-testid="bte-power-slim-tube" />
                      <span className="font-medium text-sm">Power SlimTube</span>
                    </label>
                  </RadioGroup>
                </div>
              )}

              {form.bteBrandSpecificEnabled && MARQUES_OTICON_GROUP.includes(form.marque) && (
                <div className="mt-3 ml-1 p-3 border border-zinc-200 rounded-sm bg-zinc-50/50 space-y-3" data-testid="bte-brand-demant">
                  <Label className="label-overline mb-2 block">Groupe Demant (Oticon / Bernafon / Philips)</Label>
                  <RadioGroup value={form.bteBrandSpecificType} onValueChange={form.setBteBrandSpecificType} className="space-y-2">
                    <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors bg-white ${form.bteBrandSpecificType === 'corda_minifit' ? 'border-zinc-900' : 'border-zinc-200 hover:border-zinc-400'}`}>
                      <RadioGroupItem value="corda_minifit" id="bte-corda-minifit" data-testid="bte-corda-minifit" />
                      <span className="font-medium text-sm">Corda MiniFit</span>
                    </label>
                  </RadioGroup>
                  {form.bteBrandSpecificType === 'corda_minifit' && (
                    <div className="pl-1">
                      <Label className="label-overline mb-1 block">Diamètre</Label>
                      <RadioGroup value={form.bteCordaMinifitDiameter} onValueChange={form.setBteCordaMinifitDiameter} className="flex gap-3">
                        <label className={`flex items-center gap-2 p-2 px-3 rounded-sm border cursor-pointer transition-colors bg-white ${form.bteCordaMinifitDiameter === '0.9' ? 'border-zinc-900' : 'border-zinc-200 hover:border-zinc-400'}`}>
                          <RadioGroupItem value="0.9" id="bte-corda-09" data-testid="bte-corda-09" />
                          <span className="text-sm">0,9 mm</span>
                        </label>
                        <label className={`flex items-center gap-2 p-2 px-3 rounded-sm border cursor-pointer transition-colors bg-white ${form.bteCordaMinifitDiameter === '1.3' ? 'border-zinc-900' : 'border-zinc-200 hover:border-zinc-400'}`}>
                          <RadioGroupItem value="1.3" id="bte-corda-13" data-testid="bte-corda-13" />
                          <span className="text-sm">1,3 mm</span>
                        </label>
                      </RadioGroup>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
